import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import type { IStatusPanelParams } from 'ag-grid-community';

import { type GridTotalStore, noStore } from './total-store';

export type GridStatusBarProps = IStatusPanelParams & {
  entityLabel?: string;
  showTotal?: boolean;
  totalStore?: GridTotalStore;
};

export function GridStatusBar({
  api,
  entityLabel = 'rows',
  showTotal = true,
  totalStore,
}: GridStatusBarProps) {
  const [gridCount, setGridCount] = useState(0);
  const store = totalStore ?? noStore;
  const externalTotal = useSyncExternalStore(store.subscribe, store.get);
  const totalCount = totalStore ? externalTotal : gridCount;
  const [selectedCount, setSelectedCount] = useState(0);
  const totalCountRef = useRef(0);

  useEffect(() => {
    const updateTotal = () => {
      const state = api.getServerSideGroupLevelState?.();
      const count = state?.[0]?.rowCount ?? 0;
      setGridCount(count);
    };

    const updateSelected = () => {
      try {
        const selState = api.getServerSideSelectionState() as {
          selectAll: boolean;
          toggledNodes: string[];
        };

        if (selState?.selectAll) {
          setSelectedCount(
            totalCountRef.current - (selState.toggledNodes?.length ?? 0),
          );
        } else {
          setSelectedCount(
            selState?.toggledNodes?.length ?? api.getSelectedRows().length,
          );
        }
      } catch {
        setSelectedCount(api.getSelectedRows().length);
      }
    };

    updateTotal();
    updateSelected();

    api.addEventListener('storeUpdated', updateTotal);
    api.addEventListener('selectionChanged', updateSelected);

    return () => {
      api.removeEventListener('storeUpdated', updateTotal);
      api.removeEventListener('selectionChanged', updateSelected);
    };
  }, [api]);

  useEffect(() => {
    totalCountRef.current = totalCount ?? 0;
  }, [totalCount]);

  return (
    <div className="flex items-center gap-4 px-3 py-1 text-sm text-gray-600 dark:text-gray-400">
      {showTotal && (
        <>
          <span>
            <span className="font-semibold">Total:</span>{' '}
            <span className="tabular-nums">{totalCount ?? '…'}</span>{' '}
            {entityLabel}
          </span>
          <span className="text-gray-300 select-none dark:text-gray-600">
            |
          </span>
        </>
      )}
      <span>
        <span className="font-semibold">Selected:</span>{' '}
        <span className="tabular-nums">{selectedCount}</span>
      </span>
    </div>
  );
}
